import { useState, useCallback, useEffect, useRef } from 'react';

interface UsePaginationProps {
  initialPageSize: number;
  facetFilters: string[][];
  resetPageOnFilterChange?: boolean; // Optional flag to control reset behavior
}

/**
 * Custom hook for managing pagination state and operations
 */
const usePagination = ({
  initialPageSize,
  facetFilters,
  resetPageOnFilterChange = true,
}: UsePaginationProps) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const previousFiltersRef = useRef<string[][]>(facetFilters);

  // Reset to first page when filters change (with deep comparison)
  useEffect(() => {
    if (!resetPageOnFilterChange) {
      return;
    }

    const filtersChanged =
      JSON.stringify(previousFiltersRef.current) !== JSON.stringify(facetFilters);

    if (filtersChanged) {
      setCurrentPage(0);
      previousFiltersRef.current = facetFilters;
    }
  }, [facetFilters, resetPageOnFilterChange]);

  const handlePageSizeChange = useCallback((value: string | number | object) => {
    const newSize = Number(value);
    setPageSize(newSize);
    setCurrentPage(0); // Reset to first page when changing page size
  }, []);

  return {
    currentPage,
    setCurrentPage,
    pageSize,
    handlePageSizeChange,
  };
};

export default usePagination;
